/**
 * Recursively marks all fields in a nested object or array as "touched" by setting their value to `true`.
 *
 * Typically used in Formik or similar form libraries to programmatically mark all fields as touched for validation purposes.
 *
 * @param obj - The object or array to process.
 * @returns A new object or array with the same structure, where all non-object/array leaves are replaced with `true`.
 *
 * @example
 * markTouchedRecursively({
 *   name: '',
 *   address: {
 *     city: '',
 *     zip: ''
 *   },
 *   tags: ['', '']
 * })
 * // Returns:
 * // {
 * //   name: true,
 * //   address: { city: true, zip: true },
 * //   tags: [true, true]
 * // }
 */
export const markTouchedRecursively = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(markTouchedRecursively)
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = markTouchedRecursively(obj[key])
      return acc
    }, {} as Record<string, any>)
  } else {
    return true
  }
}
